import React, { useContext } from 'react';

import useMedications from '@/hooks/useMedications';
import PersonContext from '@/pages/Person/PersonContext';
import type { Prescription } from '@/types/medication';
import { Pill, Spinner, Typography } from '@/ui';
import { DualTime } from '@/ui/DualTime';
import { isPharmacyVerified, prescriptionStatusLabel } from '@/utils/prescriptionStatus';

const joinParts = (parts: (string | null | undefined)[]) => parts.filter(Boolean).join(' · ');

// "50 mg" and "tablet" read as one strength, so they join on a space rather than a dot.
const strength = (prescription: Prescription) =>
  [prescription.dosage, prescription.dose_form].filter(Boolean).join(' ') || null;

const detail = (text: string) => (text ? <Typography muted>{text}</Typography> : null);

const PersonMedicationsSummary = () => {
  const { person } = useContext(PersonContext);

  const {
    data: medications,
    isError,
    isPending
  } = useMedications({ person_id: person.id, show_inactive: 'false' });

  const prescriptions = medications?.data ?? [];

  return (
    <>
      {isPending ? (
        <div className="mt-2 flex justify-center py-4">
          <Spinner label="Loading medications" />
        </div>
      ) : isError ? (
        <Typography className="mt-2 text-uniform-red-600 dark:text-uniform-red-500">
          Unable to load medications.
        </Typography>
      ) : !prescriptions.length ? (
        <Typography className="mt-2" muted>
          No active medications.
        </Typography>
      ) : (
        <ul className="mt-2 divide-y divide-uniform-gray-200 dark:divide-uniform-gray-700">
          {prescriptions.map((prescription) => (
            <li className="py-2 first:pt-0 last:pb-0" key={prescription.id}>
              <div className="flex items-baseline justify-between gap-2">
                <Typography className="min-w-0 break-words" weight="bold">
                  {prescription.product_name}
                </Typography>

                <Pill
                  className="shrink-0 whitespace-nowrap"
                  color={isPharmacyVerified(prescription.status) ? 'primary' : 'warning'}
                  size="sm"
                  variant="subtle">
                  {prescriptionStatusLabel(prescription.status)}
                </Pill>
              </div>

              {detail(joinParts([strength(prescription), prescription.directions]))}

              {(prescription.prescriber_name || prescription.written_at) && (
                <Typography muted>
                  {prescription.prescriber_name}
                  {prescription.prescriber_name && prescription.written_at ? ' · ' : ''}
                  {prescription.written_at && (
                    <DualTime
                      // Inherits the muted color rather than restating it here.
                      className="text-inherit dark:text-inherit"
                      content="started"
                      dateTime={new Date(prescription.written_at)}
                      showTime={false}
                    />
                  )}
                </Typography>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

export default React.memo(PersonMedicationsSummary);
